import { makeAutoObservable, reaction, runInAction } from "mobx";
import { RootStore } from "./rootStore";
import { OpenVidu, Publisher, PublisherProperties, Session, Subscriber } from "openvidu-browser";
import { BroadcastDto, BroadcastLauncherTypes, BroadcastUserDataDto, UserTypes, broadcastSettings } from "../models/broadcast";
import agent from "../api/agent";
import { toast } from "react-toastify";
import { getToastTranslation } from "../helpers/translationHelper";
import WebcamManager from "../helpers/webcamManager";
import { history } from '../..';

export default class BroadcastStore {
  rootStore: RootStore;
  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);

    reaction(
      () => this.microphoneStationId,
      (newValue : string) => {
        localStorage.setItem('stationId', newValue);
      }
    );
    reaction(
      () => this.isInternalStation,
      (newValue : boolean) => {
        localStorage.setItem('isInternalStation', newValue.toString());
      }
    );

  }
  meetingTitle : string = "";
  OV = new OpenVidu();
  webcamManager :WebcamManager | null = null;
  session : Session | undefined = undefined;
  sessionId : string = "";
  subscribers : Subscriber[] = [];
  publisher : Publisher | undefined = undefined;
  isSpeaking : boolean = false;
  isBroadcastControllerOpen : boolean = false;
  isMicrophoneActive : boolean = true;
  isCameraActive : boolean = true;
  isGrabberActive : boolean | undefined = undefined;
  streamingAvailable : boolean = true;
  authorizedCamera = false;
  authorizedMicrophone = false;
  canEditBroadcast = false;
  forceLogout = false;

  broadcastSettings : broadcastSettings = {
      microphoneDevice: undefined,
      cameraDevice: undefined,
      microphoneThreshold: -50
    }
  broadcastLauncherTypes : string | undefined = localStorage.getItem('LauncherMode_') ?? undefined;
  stopPollingTimer : boolean = false;
  pollingTimeout : number | null = null;
  pollingInterval : number = 1000; // 1 sec

  // 100 - 95 = 5 => ogni 5 chiamate aggiorna l'attività dell'utente (NB: l'attuale tolleranza sul BE è 8 sec)
  initialActivityCounter  : number = 3;
  activityCounter : number = 3;
  
  // numero di tentativi da ripetere in caso il polling non aggiorni broadcast status (es interruzione di connessione)
  // l'intervallo in questo caso sarà il triplo di pollingInterval 
  retryNumber : number = 9999999999; 
  connecting : boolean = false;
  userChoiseModalOpen : boolean = false;
  userType : UserTypes = UserTypes.undefined;
  isInternalStation : boolean = localStorage.getItem('isInternalStation') === "true" || false;
  microphoneStationId : string = localStorage.getItem('stationId') || "";
  broadcastUser : BroadcastUserDataDto = new BroadcastUserDataDto();
  connectionId : string | undefined = undefined;
  broadcastStatus : BroadcastDto | null = null;
  chairman : BroadcastUserDataDto | undefined = undefined;
  grabbersList : BroadcastUserDataDto[] = [];
  internalSpeakingList : BroadcastUserDataDto[] = []; // not used at now
  internalRaisedhands : BroadcastUserDataDto[] = [];
  externalRaisedhands : BroadcastUserDataDto[] = [];
  internalLoweredhands : BroadcastUserDataDto[] = [];
  externalLoweredhands : BroadcastUserDataDto[] = [];
  absentUsersList : BroadcastUserDataDto[] = [];
  
  // Queste sono tutte le proprietà di cui sarà registrata la modifica durante il polling
  //in questo caso ad esempio se dovesse cambiare l'email di un utente continuerà ad essere mostrata quella vecchia fino alla riconnessione dell'utente.
  
  //qui vanno solo le proprietà che non causano un movimento dell'utente tra le liste (NB: handRaisedPriority lo causa ma qui viene usata per far comparire la manina alzata solamente)
  // mentre broadcastUserTypeId ha effetto solo per i grabber in questa posizione 
  propertiesToCheck = ['handRaisedPriority',"broadcastUserTypeId","isSpeaking"]; 
  
  get needWebcam() {
    return this.broadcastLauncherTypes !== BroadcastLauncherTypes.onlyExternalSystem.toString() && !this.isInternalStation;
  }

  updateLauncherMode = (broadcastLauncherTypes? : BroadcastLauncherTypes) => {
    if(broadcastLauncherTypes !== undefined && broadcastLauncherTypes !== null){
      localStorage.setItem('LauncherMode_', broadcastLauncherTypes.toString())
      this.broadcastLauncherTypes = broadcastLauncherTypes.toString();
      this.launcherMode();
    }else {
      localStorage.setItem('LauncherMode_', "undefined")
      this.broadcastLauncherTypes = undefined;
    }
  };
 
  checkPermissions = async () => {
    const webcamManager = new WebcamManager(); // Crea un'istanza di WebcamManager
  
    try {
      if (
        this.broadcastLauncherTypes === BroadcastLauncherTypes.onlyExternalSystem.toString() ||
        this.isInternalStation
      ) {
        this.toggleAuthorizedCamera(true);
        this.toggleAuthorizedMicrophone(true);
      } else {
        // Attiva la webcam e il microfono utilizzando WebcamManager
        await webcamManager.activateWebcam();
  
        // Verifica lo stato della webcam e del microfono dopo l'attivazione
        if (webcamManager.isVideoActive) {
          this.toggleAuthorizedCamera(true);
        }
        if (webcamManager.isAudioActive) {
          this.toggleAuthorizedMicrophone(true);
        }
        runInAction(()=> {
          this.webcamManager = webcamManager;
        })
      }
    } catch (error: any) {
      if (error && (error.name === 'NotAllowedError' || error.name === 'NotFoundError')) {
        this.toggleAuthorizedCamera(false);
        this.toggleAuthorizedMicrophone(false);
      } else {
        console.error('Error accessing media devices:', error);
      }
    } finally {
      // Assicurati di disattivare la webcam e il microfono in ogni caso
      webcamManager.deactivateWebcam();
    }
  };

  launcherMode = async () => {
  if(this.isInternalStation){
    this.broadcastLauncherTypes = BroadcastLauncherTypes.onlyExternalSystem.toString();
  }
  if(parseInt(this.broadcastLauncherTypes!) === BroadcastLauncherTypes.userCanChoose){
    this.toggleUserChoideModalOpen(true);
    return;
  }
  await this.checkPermissions().then(async x => {
    if(this.authorizedCamera && this.authorizedMicrophone){
        switch (parseInt(this.broadcastLauncherTypes!)) {
          // case BroadcastLauncherTypes.userCanChoose:
          //   this.toggleUserChoideModalOpen(true);
          //   break;
          case BroadcastLauncherTypes.onlyExternalSystem:  
            // in questo caso  potranno essere interni con microfonia esterna o esterni tramite altre piattaforme (es. webex)
            this.updateUserType(false);
            await this.leaveSession();
            this.toggleConnecting(true);
            await this.joinSession().then(()=> {
            });
            break;
          case BroadcastLauncherTypes.onlyOpenVidu:
            this.updateUserType(true);
            await this.leaveSession();
            this.toggleConnecting(true);
            await this.joinSession().then(()=> {
              
            });
            break;
          default:
            break;
        }
      }
    });
  };

  toggleConnecting = (value? : boolean) => {
    if(value !== undefined)
    this.connecting = value;
    else
    this.connecting = !this.connecting;
  };
  toggleAuthorizedCamera = (value? : boolean) => {
    if(value !== undefined)
    this.authorizedCamera = value;
    else
    this.authorizedCamera = !this.authorizedCamera;
  };
  toggleAuthorizedMicrophone = (value? : boolean) => {
    if(value !== undefined)
    this.authorizedMicrophone = value;
    else
    this.authorizedMicrophone = !this.authorizedMicrophone;
  };
  
  toggleIsBroadcastControllerOpen = (value? : boolean) => {
    if(value !== undefined)
    this.isBroadcastControllerOpen = value;
    else
    this.isBroadcastControllerOpen = !this.isBroadcastControllerOpen;
      // serve per ricalcolare zwibbler e lo zoomportion quando apre e chiude il pannello del broadcast
      this.rootStore.zwibblerStore.zwiblerOverlayResizeZoomPortion();
  };

  toggleUserChoideModalOpen = (value? : boolean) => {
    if(value !== undefined)
    this.userChoiseModalOpen = value;
    else
    this.userChoiseModalOpen = !this.userChoiseModalOpen;
  };

  setStation = (isInternalStation : boolean, microphoneStationId : string) => {
  this.isInternalStation = isInternalStation;
  this.microphoneStationId = microphoneStationId;

  console.log(this.isInternalStation + " - " + this.microphoneStationId)
  };
  updateSubscribers = (subscriber? : Subscriber, ConnectionIdToremove? : string) => {
    if(!ConnectionIdToremove && subscriber){
      if(!this.subscribers.some(x => 
        JSON.parse(x.stream.connection.data).userId === JSON.parse(subscriber.stream.connection.data).userId
        && JSON.parse(x.stream.connection.data).userTypes === JSON.parse(subscriber.stream.connection.data).userTypes))
        console.log("subscribers aggiunto: " + subscriber!.stream.connection.data);
        this.subscribers = [...this.subscribers, subscriber];
    } else
      this.subscribers = this.subscribers.filter((subscriber) => subscriber.stream.connection.connectionId !== ConnectionIdToremove);
      console.log("subscribers cancellato: " + subscriber!.stream.connection.data);
  }

  toggleStreamingAvailable =(value? : boolean) =>{
    if(value !== undefined)
      this.streamingAvailable = value;
    else
      this.streamingAvailable = !this.streamingAvailable;
  }
  waitStreamingAfterReconnection = (subscribers : Subscriber[], retryNumber: number) => {
    let availableStreaming = 0;
    subscribers.forEach( subscriber => {
      let streamMedia = subscriber.stream.getMediaStream();
      if (streamMedia !== undefined)
        availableStreaming ++;
    })
    if(retryNumber < 60){
      if(availableStreaming < subscribers.length){
        setTimeout(() => {
          this.waitStreamingAfterReconnection(subscribers, retryNumber + 1);
        }, 1000);
          return;
      }else{
          this.toggleStreamingAvailable(true)
          console.log("succesfully reloaded streams")
          return;
      }
    }else {
      console.warn("failed to load stream")
    }
  }
  waitStreaming = (subscriber : Subscriber, data : any, retryNumber: number) => {
    let streamMedia = subscriber.stream.getMediaStream();
    if (!streamMedia && retryNumber < 60) {
      setTimeout(() => {
        this.waitStreaming(subscriber, data, retryNumber + 1);
    }, 1000);
      return;
    }else{
      if(retryNumber >= 60){
        console.warn("failed to load stream")
      } else {
        if(data){
          if(data.userType && ([UserTypes.externalOpenvidu, UserTypes.grabber, UserTypes.internalOpenvidu].some(x => x === data.userType)))
          this.updateSubscribers(subscriber);
        }
        console.log("succesfully loaded stream")
      }
    }
  }
  waitConnectionId = ( retryNumber: number) => {
    if(this.publisher ){ 
      let connectionId = this.publisher.stream.connection.connectionId;
      if (!connectionId && retryNumber < 60) {
        setTimeout(() => {
          this.waitConnectionId(retryNumber + 1);
      }, 1000);
        return;
      }else{
        if(retryNumber >= 60){
          console.warn("failed to load connction id for user = " + JSON.parse(this.publisher.stream.connection.data).userId)
        } else {
          this.updateConnectionId(connectionId);
          }
          console.log("succesfully loaded stream")
        }
    }else setTimeout(() => {
      this.waitConnectionId(retryNumber + 1);
  }, 1000);
    return;
    }
  joinSession = async (skipTimer? : boolean) => {
      var sessionId = "meetingRoom_" + this.rootStore.meetingStore.sourceId?.toString();
      this.meetingTitle = this.rootStore.meetingStore.currentTree?.title ?? "";
      this.sessionId = sessionId;
     
      this.OV = new OpenVidu();
      this.session = this.OV.initSession();
      this.OV.setAdvancedConfiguration({
        // forceMediaReconnectionAfterNetworkDrop: true,
        publisherSpeakingEventsOptions: {
            interval: 50,   // Frequency of the polling of audio streams in ms (default 100)
            threshold: this.broadcastSettings.microphoneThreshold  // Threshold volume in dB (default -50)
        }
      });
  
      this.session.on('streamCreated', (event) => {
        const data = JSON.parse(event.stream.connection.data);
        if(this.broadcastUser.userId !== data.userId || !this.subscribers.some(x => JSON.parse(x.stream.connection.data).userId === data.userId && JSON.parse(x.stream.connection.data).userType === data.userType)){
            const subscriber = this.session!.subscribe(event.stream, 'subscriber-div');
            this.waitStreaming(subscriber, data , 0);
        }
      });
      this.session.on('streamPropertyChanged', (event) => {
        if(event.changedProperty === 'videoActive' || event.changedProperty === 'audioActive'){
          const data = JSON.parse(event.stream.connection.data);
          let subscriberIndex = this.subscribers.findIndex(x => JSON.parse(x.stream.connection.data).userId === data.userId && JSON.parse(x.stream.connection.data).userType === data.userType);
          console.log("changedValue")
          if(subscriberIndex !== -1){
            let subscriber = this.subscribers[subscriberIndex];
            this.updateSubscribers(subscriber, subscriber.stream.connection.connectionId);
            if(event.changedProperty === 'videoActive'){
              subscriber.stream.hasVideo = !!event.newValue;
            }else if(event.changedProperty === 'audioActive'){
              subscriber.stream.hasAudio = !!event.newValue;
            }
            this.updateSubscribers(subscriber, undefined);
          }
        }
      });
  
      this.session.on('connectionCreated', (event) => {
        const data = JSON.parse(event.connection.data);
          if(data && data.userId === this.broadcastUser.userId && data.userType === this.broadcastUser.broadcastUserTypeId){
            console.log("connectionIDDD = " + event.connection.connectionId)
            if(event.connection.connectionId){
              this.updateConnectionId(event.connection.connectionId);
            }else{
              this.waitConnectionId(0)
            }
            this.toggleConnecting(false);
          }
      });
        
      this.session.on('streamDestroyed', (event) => {
        // Remove disconnected subscribers
        this.updateSubscribers(undefined, event.stream.connection.connectionId);
      });
  
      this.session.on('reconnecting', () => 
        console.log('Oops! Trying to reconnect to the session'));
      this.session.on('reconnected', () => {
        console.log('Hurray! You successfully reconnected to the session')
        this.toggleStreamingAvailable(false)
        this.waitStreamingAfterReconnection(this.subscribers, 0)
        console.log('streaming Check started')
      });
      this.session.on('sessionDisconnected', (event) => {
        if (event.reason === 'networkDisconnect') {
            console.log('Dang-it... You lost your connection to the session!');
            console.log('Reconnecting mode started');
            this.launcherMode();
            console.log('Reconnecting mode ended');
        } else {
            // Disconnected from the session for other reason than a network drop
            console.log("other reason")
        }
      });
      this.session.on('exception', (event) => {
        var stream : any = event.origin;
        if (event.name === 'ICE_CONNECTION_FAILED') {
          if (stream.hasOwnProperty('streamId')){
            console.log('Stream ' + stream.streamId + ' broke!');
            console.log('Reconnection process automatically started');
          }else 
          console.log('Stream Session broke!');
        }
        if (event.name === 'ICE_CONNECTION_DISCONNECTED') {
          if (stream.hasOwnProperty('streamId')){
            console.log('Stream ' + stream.streamId + ' disconnected!');
            console.log('Giving it some time to be restored. If not possible, reconnection process will start');
          }else 
          console.log('Stream Session disconnected!!');
        }
    });
      console.log("Connection started : meetingRoom_" + this.rootStore.meetingStore.sourceId?.toString())
    
    if (this.OV && this.session !== undefined) {
      try 
      {
        this.getToken().then(async (token) => {
          if (token) {
            const connectionData = JSON.stringify({ userId: this.rootStore.userStore.user?.id, userType: this.userType });
            await this.session!.connect(token, connectionData)
          .then(async () => {
            if(this.userType !== UserTypes.internal && this.userType !== UserTypes.external){
              // --- 5) Get your own camera stream ---
              // Init a publisher passing undefined as targetElement (we don't want OpenVidu to insert a video
              // element: we will manage it on our own) and with the desired properties
              let publisher = await this.OV.initPublisherAsync(undefined, {
                  audioSource: this.broadcastSettings.microphoneDevice?.deviceId ?? undefined, // The source of audio. If undefined default microphone
                  videoSource: this.broadcastSettings.cameraDevice?.deviceId ?? undefined, // The source of video. If undefined default webcam
                  publishAudio: this.isMicrophoneActive, // Whether you want to start publishing with your audio unmuted or not
                  publishVideo: this.isCameraActive, // Whether you want to start publishing with your video enabled or not
                  resolution: '640x480', // The resolution of your video
                  frameRate: 30, // The frame rate of your video
                  insertMode: 'APPEND', // How the video is inserted in the target element 'video-container'
                  mirror: true, // Whether to mirror your local video or not
              });
              
                // publisher.on('publisherStartSpeaking', (event) => {
                //     console.log('User ' + event.connection.connectionId + ' start speaking');
                //     this.toggeIsSpeaking();
                //     this.toggleStreamLoaded();

                // });
              
                // publisher.on('publisherStopSpeaking', (event) => {
                //     console.log('User ' + event.connection.connectionId + ' stop speaking');
                //     this.toggeIsSpeaking();
                //     this.toggleStreamLoaded();

                // });
              // --- 6) Publish your stream ---
    
              this.session!.publish(publisher);
    
              // Obtain the current video device in use
              var devices = await this.OV.getDevices();
              if(devices === null || devices === undefined || devices.length === 0){
                toast.error("accesso audio/video negati.")
                await this.leaveSession();
              }else{
                var videoDevices = devices.filter(device => device.kind === 'videoinput');
                if(videoDevices.length === 0){
                  toast.error("nessun accesso video trovato.")
                }
                var currentVideoDeviceId = publisher.stream.getMediaStream().getVideoTracks()[0].getSettings().deviceId;
                var currentVideoDevice = videoDevices.find(device => device.deviceId === currentVideoDeviceId);
      
                // Obtain the current audio device in use
                var audioDevices = devices.filter(device => device.kind === 'audioinput');
                if(audioDevices.length === 0){
                  toast.error("nessun accesso audio trovato.")
                }
                var currentAudioDeviceId = publisher.stream.getMediaStream().getAudioTracks()[0].getSettings().deviceId;
                var currentAudioDevice = audioDevices.find(device => device.deviceId === currentAudioDeviceId);
              }

              let broadcastSettings : broadcastSettings= {
                ...this.broadcastSettings,
                microphoneDevice: currentAudioDevice,
                cameraDevice: currentVideoDevice,
              }
              this.updateBroadcastSettings(broadcastSettings);
              this.publisher = publisher;
            }
          })
        }
        });
      } catch (error: any) {
        console.log(error)
      }
    }
     
    if(!skipTimer){
      if(this.stopPollingTimer)
        this.stopPollingTimer = false;
        this.UpdateTimeout(); // avvia il polling
    }
  };
  
 
  updateConnectionId = (connectionId : string ) => {
    this.connectionId = connectionId;
    console.log("registro il connection ID : " + connectionId)
      runInAction(() => {
      this.broadcastUser = {...this.broadcastUser, connectionId : connectionId};
      })
  }

  updateBroadcastSettings = (broadcastSetting : broadcastSettings) => {
    this.broadcastSettings = {
      ...this.broadcastSettings,
      ...broadcastSetting
    }
  }
  leaveSession = async (isSessionClosed? : boolean) => {
    try {
      this.stopPolling();
      console.log("disconnecting")

      if(!isSessionClosed && this.sessionId){
        if(this.session){
        // this.session.disconnect();
          if( this.publisher){
          this.session.unpublish(this.publisher);
            this.publisher.stream.disposeWebRtcPeer();
            this.publisher.stream.disposeMediaStream();
            if(this.webcamManager !== null){
              this.webcamManager.deactivateWebcam();
              // this.toggleAuthorizedCamera(false);
              // this.toggleAuthorizedMicrophone(false);
            }
          }
        }
        await agent.Broadcast.leaveSession(this.sessionId, [this.broadcastUser])
      }
      runInAction(()=> {
        this.session = undefined;
        this.chairman = undefined;
        this.subscribers = [];
        this.publisher = undefined;
        // this.isMicrophoneActive = true;
        // this.isCameraActive = true;
        this.broadcastStatus = null;
        this.broadcastUser = new BroadcastUserDataDto();
        this.connectionId = undefined;
        this.isGrabberActive = undefined;
        this.internalRaisedhands = [];
        this.externalRaisedhands = [];
        this.externalLoweredhands = [];
        this.internalLoweredhands = [];
        this.grabbersList = [];
        this.absentUsersList = [];
      })
    } catch (error : any) {
      console.log(error)
      if (error && error.data && error.data.errors && error.data.errors.message) {
        toast.error(error.data.errors.message);
      }
    }
  };

  updateSetting = async (newSettings: broadcastSettings) => {
  if(this.publisher){
    if(newSettings.microphoneDevice?.deviceId !== this.broadcastSettings.microphoneDevice?.deviceId){
      let properties : PublisherProperties = {
        audioSource: newSettings.microphoneDevice?.deviceId
      }
      let mediaStream = await this.OV.getUserMedia(properties);

      // Getting the audio track from mediaStream
      let myAudioTrack = mediaStream.getAudioTracks()[0];
      this.publisher.replaceTrack(myAudioTrack)
      .then(() => console.log('New audiotrack has been published'))
      .catch(error => console.error('Error replacing audiotrack'));
    }

    if(newSettings.cameraDevice?.deviceId !== this.broadcastSettings.cameraDevice?.deviceId){
      let properties : PublisherProperties = {
        videoSource: newSettings.cameraDevice?.deviceId
      }
      let mediaStream = await this.OV.getUserMedia(properties);

      // Getting the video track from mediaStream
      let myVideoTrack = mediaStream.getVideoTracks()[0];
      this.publisher.replaceTrack(myVideoTrack)
      .then(() => console.log('New videotrack has been published'))
      .catch(error => console.error('Error replacing videotrack'));
    }

    if(newSettings.microphoneThreshold !== this.broadcastSettings.microphoneThreshold){
      this.publisher.updatePublisherSpeakingEventsOptions({
            interval: 50,   // Frequency of the polling of audio streams in ms
            threshold: newSettings.microphoneThreshold  // Threshold volume in dB
          });
    }
  }
    // console.log("new broadcastSetting: " + this.broadcastSettings);
  runInAction(()=>{
    this.updateBroadcastSettings(newSettings);
  })
  };

  //  // Funzione per rimuovere un subscriber specifico
  //  removeSubscriber = (subscriberToRemove : Subscriber) => {
  //     if (this.session) {
  //         const updatedSubscribers = this.subscribers.filter(
  //             (subscriber) => subscriber.stream.connection.connectionId !== subscriberToRemove.stream.connection.connectionId
  //         );
  //         this.subscribers = updatedSubscribers;
  //     }
  //  };

  UpdateTimeout = (value? : number | null) => {
     if(this.pollingTimeout !== null){
        clearTimeout(this.pollingTimeout);
        this.pollingTimeout = null;
      }
      if(!value){
        this.pollingTimeout = setTimeout(this.synchronizeBroadcastStatus, this.pollingInterval);
      }
      else
        this.pollingTimeout = value;
  };

  synchronizeBroadcastStatus = async (retryCount: number = this.retryNumber) => {
    if(!this.stopPollingTimer)
      try {
        if(this.sessionId !== "" && this.broadcastStatus){
          let connectionId = (this.activityCounter === this.initialActivityCounter) && this.connectionId ? 
            this.connectionId : this.broadcastUser.broadcastUserTypeId === UserTypes.internal ? "internalUser" : "";
          await agent.Broadcast.synchronizeSession(this.sessionId, this.broadcastStatus.lastUpdate, connectionId).then((synchronizedDto)=>{
            if(!synchronizedDto.isSynchronized){
              runInAction(()=> {
                this.broadcastStatus = synchronizedDto.broadcastStatus;
                this.broadcastUser = synchronizedDto.broadcastStatus.userList[0];
                this.broadcastUser.connectionId = this.connectionId;
                this.updateBroadcastStatus(synchronizedDto.broadcastStatus.userList);
                if(this.grabbersList.length < 1)
                  this.isGrabberActive = undefined;
                //in caso di disconnessione riconnette l'utente
                if(this.broadcastUser.broadcastUserTypeId === UserTypes.undefined && this.connecting === false){
                  // this.toggleConnecting(true);
                  // this.leaveSession().then(async ()=> {
                  //   await this.joinSession(true)
                  // });
                  this.launcherMode();
                  return;
                }
              })
            }
          });
        }
        this.updateActivityCounter();
        this.UpdateTimeout();
      } catch (error : any) {
        if(error && error.status === 404){
          toast.error(getToastTranslation("closedMeeting"))
          await this.leaveSession(true);
          if(this.forceLogout)
            history.push('/expired');
        }else {
          // Gestisci l'errore, ad esempio, con console.error
          console.error('Errore durante la sincronizzazione del broadcast');

          // Se ci sono tentativi rimanenti, ritenta dopo 2 secondi
          if (retryCount > 0 && !this.stopPollingTimer) {
            setTimeout(() => {
              this.synchronizeBroadcastStatus(retryCount - 1);
            }, (this.pollingInterval*3));
          }
        }
      }
  };

  updateActivityCounter = () => {
    if(this.activityCounter === this.initialActivityCounter){
      this.activityCounter = 0 ;
    } else {
      this.activityCounter = this.activityCounter + 1;
    }
  }

  stopPolling = () => {
    this.stopPollingTimer = true;
    runInAction(() => {
      if (this.pollingTimeout) {
        clearTimeout(this.pollingTimeout);
        this.pollingTimeout = null;
      }
    })
  };

  toggleHandRaised = (grabber? : BroadcastUserDataDto)=>{
    if(grabber !== undefined){
      const handProperty = grabber.handRaisedPriority = grabber.handRaisedPriority === 0 ? 1 : 0;
      const localGrabberIndex = this.grabbersList.findIndex(localGrabber => localGrabber.grabberId === grabber.grabberId);
          if (localGrabberIndex !== -1) {
            // Aggiorna i dati dell'utente senza spostarlo di lista
            this.grabbersList[localGrabberIndex].handRaisedPriority = handProperty;
          }
      this.updateSession(grabber);
    }else {
      this.broadcastUser.handRaisedPriority = this.broadcastUser.handRaisedPriority === 0 ? 1 : 0;
      this.updateSession(this.broadcastUser);
    }
  };

  updateSession = async (broadcastUserData : BroadcastUserDataDto) => {
    if(this.sessionId !== ""){
      try {
        await agent.Broadcast.updateSession(this.sessionId, broadcastUserData)
        // runInAction(()=> {
        //   this.broadcastStatus = broadcastDto;
        //   this.broadcastUser = broadcastDto.broadcastStatus[0];
        // })
      } catch (error : any ) {
        console.log(error)
        if (error && error.data && error.data.errors && error.data.errors.message) {
          toast.error(error.data.errors.message);
        }
      }
    }else{
      console.log("Session undefined")
    }
  };

  toggeIsSpeaking = () => {
  this.isSpeaking = !this.isSpeaking;
  };

  toggleAllGrabbers = (isActivatingGrabbers : boolean) => {
    let aspectedValue = UserTypes.undefined
    if(!isActivatingGrabbers){
      aspectedValue = UserTypes.undefined;
    }else{
      aspectedValue = UserTypes.grabber;
    }
    this.grabbersList.forEach(async grabber => {
      if(grabber.broadcastUserTypeId !== aspectedValue){
        // grabber.broadcastUserTypeId = aspectedValue;
        let grabberToUpdate = {...grabber, broadcastUserTypeId: aspectedValue};
        try {
          await this.updateSession(grabberToUpdate);
        } catch (error) {
          let grabberIndex = this.grabbersList.findIndex(x => x.grabberId === grabber.grabberId);
          let updatedGrabbersList = [...this.grabbersList];

          // Aggiornare il grabber esistente con un errore
          updatedGrabbersList[grabberIndex] = {
            ...updatedGrabbersList[grabberIndex],
            error: "Error requires manual Intervention" // Aggiungi il messaggio di errore personalizzato qui
          };
          this.grabbersList = updatedGrabbersList;
        }
      }
    })
  };
      
  toggleAudio = async () => {
    if (this.publisher) {
        this.isMicrophoneActive = !this.isMicrophoneActive;
        await this.publisher.publishAudio(this.isMicrophoneActive);
    }
  };

  toggleVideo = async () => {
    if (this.publisher) {
        this.isCameraActive = !this.isCameraActive;
        await this.publisher.publishVideo(this.isCameraActive);
    }
  };
  
  // toggleStreamLoaded = () => {
  //   this.streamLoaded = !this.streamLoaded;
  // };

  closeSession = async () => {
    try {
      await agent.Broadcast.closeSession(this.sessionId).then(async () => {
        await this.leaveSession(true);
        if(this.forceLogout)
          history.push('/expired');
      });
    } catch (error : any) {
      console.log(error)
      if (error && error.data && error.data.errors && error.data.errors.message) {
        toast.error(error.data.errors.message);
      }
    }
  };

  getToken = async() => {
    var sessionId = await this.createSession();
    return await this.connectToSession(sessionId);
  };
    
  updateUserType = (usingTeletabber : boolean) => {
  this.userType = this.isInternalStation ? UserTypes.internal : usingTeletabber ? UserTypes.externalOpenvidu : UserTypes.external
  };

  createSession = async() => {
    try 
    {
      let meetingId = 0;
      if(this.rootStore.meetingStore.sourceId){
        meetingId = this.rootStore.meetingStore.sourceId;
      }else console.log("MeetingId undefined")
      const broadcastSessionId = await agent.Broadcast.createSession(this.sessionId, meetingId, this.userType);
      console.log("createSession: " + broadcastSessionId)
      return broadcastSessionId; // The sessionId
      
    } catch (error : any) {
      this.session = undefined;
      this.toggleConnecting(false);
      console.log(error)
      if (error && error.data && error.data.errors && error.data.errors.message) {
        toast.error(error.data.errors.message);
      }
      throw error;
    }
  };
  
  connectToSession = async(sessionId: string) => {
    try {
      const broadcastDto = await agent.Broadcast.connectToSession(sessionId, this.userType,"", this.microphoneStationId);
      runInAction(()=> {
        this.broadcastStatus = broadcastDto;
        this.forceLogout = broadcastDto.isCloseSessionMassiveLogoutEnabled;
        this.broadcastUser = broadcastDto.userList[0];
        this.broadcastUser.connectionId = this.connectionId;
        this.canEditBroadcast = this.broadcastUser.canEditBroadcast
        if((this.internalLoweredhands.length + this.externalLoweredhands.length +
          this.internalRaisedhands.length + this.externalRaisedhands.length + this.absentUsersList.length) === 0){
            this.orderUsersBetweenLists(broadcastDto.userList);
          }else{
            this.updateBroadcastStatus(broadcastDto.userList);
          }
        this.stopPollingTimer = false;
      })
      return broadcastDto.token; // The token
    } catch (error : any) {
      this.session = undefined;
      this.toggleConnecting(false);
      console.log(error)
      if (error && error.data && error.data.errors && error.data.errors.message) {
        toast.error(error.data.errors.message);
      }
      throw error;
    }
  };

  updateBroadcastStatus = (newUserList : BroadcastUserDataDto[]) => {
    var userToRemove : BroadcastUserDataDto[] = [...this.internalRaisedhands, ...this.externalRaisedhands, ...this.internalLoweredhands, ...this.externalLoweredhands, ...this.grabbersList, ...this.absentUsersList];
    var allActualUsers : BroadcastUserDataDto[] = [...this.internalRaisedhands, ...this.externalRaisedhands, ...this.internalLoweredhands, ...this.externalLoweredhands, ...this.grabbersList, ...this.absentUsersList];
    const userToMoveBetweenList: BroadcastUserDataDto[] = [];
    const userToUpdateList: BroadcastUserDataDto[] = [];
    // Itera su ogni lista e trova ed aggiorna gli utenti modificati
    [this.internalRaisedhands, this.externalRaisedhands, this.internalLoweredhands, this.externalLoweredhands, this.grabbersList, this.absentUsersList].forEach(localList => {
      newUserList.forEach(apiUser => {
        const localUserIndex = localList.findIndex(localUser => (localUser.userId !== null && localUser.userId !== undefined && localUser.userId === apiUser.userId) || (localUser.grabberId !== null && localUser.grabberId !== undefined && localUser.grabberId === apiUser.grabberId));
        
        if(localUserIndex !== -1){
          userToRemove = userToRemove.filter(localUser => (localUser.userId !== null && localUser.userId !== undefined && localUser.userId !== apiUser.userId) || (localUser.grabberId !== null && localUser.grabberId !== undefined && localUser.grabberId !== apiUser.grabberId));
        // Verifica se l'utente deve essere spostato in una lista diversa
        const userToMove = (apiUser.grabberId === null || apiUser.grabberId === undefined) &&
        (
          (localList[localUserIndex].handRaisedPriority === 0 && apiUser.handRaisedPriority !== 0) ||
          (localList[localUserIndex].handRaisedPriority !== 0 && apiUser.handRaisedPriority === 0) ||
          localList[localUserIndex].broadcastUserTypeId !== apiUser.broadcastUserTypeId ||
          localList[localUserIndex].isChairman !== apiUser.isChairman
        );

        
    
        // Confronta le proprietà specificate nell'array
        const propertiesChanged = this.propertiesToCheck.some(prop => localList[localUserIndex][prop] !== apiUser[prop]);
    
        // Aggiungi l'utente alle liste appropriate
        if (userToMove) {
          userToMoveBetweenList.push(apiUser);
          if(localList[localUserIndex].broadcastUserTypeId !== apiUser.broadcastUserTypeId){
            if(apiUser.broadcastUserTypeId === 0){
              toast.warning(apiUser.username + " " + getToastTranslation("leftSession"), {
                className: 'broadcastToast'})
            }else{
              toast.success(apiUser.username + " " + getToastTranslation("joinSession"), {
                className: 'broadcastToast'})
            }
          }
        } else if (propertiesChanged) {
          userToUpdateList.push(apiUser);

          if(apiUser.grabberId !== null){
            if(apiUser.broadcastUserTypeId === 0){
              toast.warning(apiUser.username + " disconnected")
            }else{
              toast.success(apiUser.username + " connected")
            }
          }
        } 
      }
      });

      // Aggiorna gli utenti modificati nella lista locale
      if(userToMoveBetweenList.length > 0){
        userToMoveBetweenList.forEach(modifiedUser => {
          const localUserIndex = localList.findIndex(localUser => localUser.userId === modifiedUser.userId);
          if (localUserIndex !== -1) {
             
            // Rimuovi l'utente dalla lista attuale
            localList.splice(localUserIndex, 1);
          }
        });
      }
      if(userToUpdateList.length > 0){
        userToUpdateList.forEach(modifiedUser => {
          const localUserIndex = localList.findIndex(localUser => (localUser.userId !== null && localUser.userId !== undefined && localUser.userId === modifiedUser.userId) || (localUser.grabberId !== null && localUser.grabberId !== undefined && localUser.grabberId === modifiedUser.grabberId));
          if (localUserIndex !== -1) {
            // Aggiorna i dati dell'utente senza spostarlo di lista
            localList[localUserIndex] = modifiedUser;
          }
        });
        // Ordina nuovamente la lista in base a handRaisedPriority
        localList.sort((a, b) => a.handRaisedPriority - b.handRaisedPriority);
      }
    });

    //elimino tutti gli utenti a quali è stato rimosso l'invito al broadcast
    if (userToRemove.length > 0) {
      [this.internalRaisedhands, this.externalRaisedhands, this.internalLoweredhands, this.externalLoweredhands, this.grabbersList, this.absentUsersList].forEach((localList, index) => {
        // Applica il filtro una volta per ciascuna lista, considerando tutti gli utenti da rimuovere
        const updatedList = localList.filter(localUser => 
          !userToRemove.some(actualUser =>
            (localUser.userId !== null && localUser.userId !== undefined && localUser.userId === actualUser.userId) ||
            (localUser.grabberId !== null && localUser.grabberId !== undefined && localUser.grabberId === actualUser.grabberId)
          )
        );
        // Aggiorna la lista con il nuovo contenuto filtrato
        this.replaceListByIndex(index, updatedList);
      });
    }
    // aggiungo tutti gli utenti appena invitati
    const usersToAdd = newUserList.filter(
      utente1 => !allActualUsers.some(utente2 => (utente1.userId !== null && utente1.userId !== undefined && utente1.userId === utente2.userId) || (utente1.grabberId !== null && utente1.grabberId !== undefined && utente1.grabberId === utente2.grabberId))
    );

    // Inserisci gli utenti da spostare nella lista giusta e i nuovi utenti
    this.orderUsersBetweenLists([...userToMoveBetweenList,...usersToAdd]);
  };

  orderUsersBetweenLists = (userList : BroadcastUserDataDto[], updating? : boolean) =>{
    let chairman : BroadcastUserDataDto | undefined = undefined;
    const internalRaisedhands : BroadcastUserDataDto[] = [];
    const externalRaisedhands : BroadcastUserDataDto[] = [];
    const externalLoweredhands : BroadcastUserDataDto[] = [];
    const internalLoweredhands : BroadcastUserDataDto[] = [];
    const grabbersList : BroadcastUserDataDto[] = [];
    const absentUsersList : BroadcastUserDataDto[] = [];
    userList.forEach(user => {
      if (user.isChairman === true && user.broadcastUserTypeId !== UserTypes.undefined) {
        chairman = user;
        // Criterio 2: Utenti con handRaised == true e userType == 1 (internal)
      } else if (user.handRaisedPriority !== 0  && user.broadcastUserTypeId === UserTypes.internal) {
        internalRaisedhands.push(user);
        // Criterio 2: Utenti con handRaised == true e userType == 1 (internal)
      } else if (user.handRaisedPriority !== 0 && [UserTypes.external, UserTypes.externalOpenvidu, UserTypes.internalOpenvidu].includes(user.broadcastUserTypeId)) {
        externalRaisedhands.push(user);
        // Criterio 2: Utenti con handRaised == true e userType == 2, 3 o 4
      } else if (user.userId && [UserTypes.external, UserTypes.externalOpenvidu, UserTypes.internalOpenvidu].includes(user.broadcastUserTypeId)) {
        if (this.rootStore.userStore.user?.id && user.userId === this.rootStore.userStore.user?.id) {
          // Criterio 1: Utente con id = X
          externalLoweredhands.unshift(user);
        }else
        // Criterio 3: Utenti con handRaised == false, userId diverso da null e userType == 2, 3 o 4
        externalLoweredhands.push(user);
      } else if (user.userId && user.broadcastUserTypeId === UserTypes.internal) {
        if (this.rootStore.userStore.user?.id && user.userId === this.rootStore.userStore.user?.id) {
          // Criterio 1: Utente con id = X
          internalLoweredhands.unshift(user);
        }else
        // Criterio 3: Utenti con handRaised == false, userId diverso da null e userType == 1 (internal)
        internalLoweredhands.push(user);
      } else {
        // Criterio 4: Gli altri utenti
        if (user.grabberId === null && user.broadcastUserTypeId === UserTypes.undefined) {
          absentUsersList.push(user);
        }else if(user.grabberId !== null){
          let grabber = user;
          grabber.error = undefined;
          if(!this.grabbersList.some(x => x.grabberId === grabber.grabberId))
            grabbersList.push(grabber);
        }
        // altrimenti sarà la regia che non deve comparire
      }
    });
        // Ordina internalRaisedhands in base a handRaisedPriority
        internalRaisedhands.sort((a, b) => a.handRaisedPriority - b.handRaisedPriority);
        // Ordina externalRaisedhands in base a handRaisedPriority
        externalRaisedhands.sort((a, b) => a.handRaisedPriority - b.handRaisedPriority);

    // let newUsers = [...internalLoweredhands.filter(x => !this.internalLoweredhands.some(y => x.userId === y.userId) || !this.internalRaisedhands.some(y => x.userId === y.userId)),
    //   ...externalLoweredhands.filter(x => !this.externalLoweredhands.some(y => x.userId === y.userId) || !this.externalRaisedhands.some(y => x.userId === y.userId)) ]

    this.chairman = chairman;
    this.internalRaisedhands = [...this.internalRaisedhands,...internalRaisedhands];
    this.externalRaisedhands = [...this.externalRaisedhands,...externalRaisedhands];
    this.externalLoweredhands = [...this.externalLoweredhands,...externalLoweredhands];
    this.internalLoweredhands = [...this.internalLoweredhands,...internalLoweredhands];
    this.grabbersList = [...this.grabbersList,...grabbersList];
    this.absentUsersList = [...this.absentUsersList,...absentUsersList];
  };


  // comunicateChanges = (absentUsersList : BroadcastUserDataDto[],grabbersList : BroadcastUserDataDto[] ) => {
  //   let changedGrabbers = grabbersList.filter(x => this.grabbersList.some(y => x.grabberId === y.grabberId && x.broadcastUserTypeId !== y.broadcastUserTypeId))
  //   changedGrabbers.forEach(grabber => {
  //     if(grabber.broadcastUserTypeId === 0){
  //       toast.warning(grabber.username + " disconnected")
  //     }else{
  //       toast.success(grabber.username + " connected")
  //     }
  //   })
    
  //   let leftingUsers = absentUsersList.filter(x => !this.absentUsersList.some(y => x.userId === y.userId && x.broadcastUserTypeId !== y.broadcastUserTypeId))
  //   leftingUsers.forEach(user => toast.warning(user.username + " left Session"))
  //   let joiningUsers = this.absentUsersList.filter(x => !absentUsersList.some(y => x.userId === y.userId && x.broadcastUserTypeId !== y.broadcastUserTypeId))
  //   joiningUsers.forEach(user => toast.success(user.username + " join Session"))
  // }

  replaceListByIndex = (index : number, newList : BroadcastUserDataDto[]) => {
    switch (index) {
      case 0:
        this.internalRaisedhands = newList;
        break;
      case 1:
        this.externalRaisedhands = newList;
        break;
      case 2:
        this.internalLoweredhands = newList;
        break;
      case 3:
        this.externalLoweredhands = newList;
        break;
      case 4:
        this.grabbersList = newList;
        break;
      case 5:
        this.absentUsersList = newList;
        break;
      default:
        console.error("Invalid index provided");
    }
  };
}